import type { Invite, User } from '@gmn-portal/backend';
import type { SortingState } from '@tanstack/react-table';
import { Plus, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  payloadClient,
  useCreateInvite,
  useDeleteInvite,
  useInvites,
  useUpdateInvite,
  useUpdateUser,
  useUserGroups,
  useUsers,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  TableCard,
  TableCardContent,
  TableCardHeader,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { sortUserGroupsByKey } from '@/shared/utils/permissions';

import { UserForm, type UserFormValues } from '../components/user-form';
import { UserTable } from '../components/user-table';

interface PageProps {
  allowedGroupKeys: string[];
  customerId?: string;
  hideInviteButton?: boolean;
  title?: string;
  /** If true, use embedded mode without PageContainer, TableCard, and search */
  embedded?: boolean;
  /** Controlled dialog state - if provided, dialog is controlled externally */
  isInviteDialogOpen?: boolean;
  /** Callback when invite dialog open state changes */
  onInviteDialogOpenChange?: (open: boolean) => void;
  onUsageChange?: (usage: { userCount: number; inviteCount: number }) => void;
}

export function UserManagement({
  allowedGroupKeys,
  customerId,
  hideInviteButton,
  title,
  embedded = false,
  isInviteDialogOpen: controlledIsOpen,
  onInviteDialogOpenChange,
  onUsageChange,
}: PageProps) {
  const { t } = useIntl();
  const { user: currentUser, updateUser: updateAuthUser } = useAuth();
  const [page] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived'>('active');

  const resolvedCustomerId = customerId
    ? customerId
    : currentUser?.customer
      ? typeof currentUser.customer === 'string'
        ? currentUser.customer
        : currentUser.customer.id
      : undefined;

  const tablePreferences = useTablePreferences({
    tableId: 'user-management',
    defaultPreferences: {
      sorting: [],
      searchTerm: '',
    },
  });

  // Load initial preferences
  const preferences = tablePreferences.loadPreferences();
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Save preferences when they change
  useEffect(() => {
    tablePreferences.savePreferences({
      sorting,
      searchTerm,
    });
  }, [sorting, searchTerm, tablePreferences]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const { data: userGroupsData, isLoading: isLoadingGroups } = useUserGroups({
    limit: 0,
    sort: 'key',
  });

  const allowedGroups = sortUserGroupsByKey(
    (userGroupsData?.docs || []).filter((group) => allowedGroupKeys.includes(group.key))
  );

  const allowedGroupIds = allowedGroups.map((group) => group.id);
  const customerScopeWhere = resolvedCustomerId
    ? { customer: { equals: resolvedCustomerId } }
    : { customer: { exists: false } };
  const userStatusWhere =
    statusFilter === 'archived'
      ? { archived: { equals: true } }
      : { archived: { not_equals: true } };

  const scopedUserWhere =
    allowedGroupIds.length > 0
      ? {
          and: [
            {
              userGroup: {
                in: allowedGroupIds,
              },
            },
            customerScopeWhere,
            userStatusWhere,
          ],
        }
      : undefined;

  const scopedInviteWhere =
    allowedGroupIds.length > 0
      ? {
          and: [
            {
              userGroup: {
                in: allowedGroupIds,
              },
            },
            customerScopeWhere,
          ],
        }
      : undefined;

  const usageWhere = {
    and: [customerScopeWhere, { archived: { not_equals: true } }],
  };
  const usageInviteWhere = customerScopeWhere;
  const usageEnabled = Boolean(resolvedCustomerId && onUsageChange);

  const { data: usersData, isLoading: isLoadingUsers } = useUsers({
    page,
    limit: 0,
    sort: 'createdAt',
    where: scopedUserWhere,
    depth: 1,
    disabled: isLoadingGroups || allowedGroupIds.length === 0,
  });

  const { data: invitesData, isLoading: isLoadingInvites } = useInvites({
    page,
    limit: 0,
    sort: 'createdAt',
    where: scopedInviteWhere,
    depth: 1,
    disabled: statusFilter === 'archived' || isLoadingGroups || allowedGroupIds.length === 0,
  });

  const { data: usageUsersData } = useUsers({
    page,
    limit: 0,
    sort: 'createdAt',
    where: usageWhere,
    depth: 0,
    disabled: !usageEnabled,
  });

  const { data: usageInvitesData } = useInvites({
    page,
    limit: 0,
    sort: 'createdAt',
    where: usageInviteWhere,
    depth: 0,
    disabled: !usageEnabled,
  });

  const userCount = usersData?.totalDocs ?? 0;
  const inviteCount = invitesData?.totalDocs ?? 0;
  const usageUserCount = usageUsersData?.totalDocs ?? userCount;
  const usageInviteCount = usageInvitesData?.totalDocs ?? inviteCount;

  useEffect(() => {
    if (!resolvedCustomerId || !onUsageChange) {
      return;
    }
    onUsageChange({ userCount: usageUserCount, inviteCount: usageInviteCount });
  }, [resolvedCustomerId, onUsageChange, usageUserCount, usageInviteCount]);

  const [selectedUser, setSelectedUser] = useState<
    User | (Invite & { isInvite: true }) | undefined
  >(undefined);
  // Support both controlled and uncontrolled dialog state
  const [internalIsInviteDialogOpen, setInternalIsInviteDialogOpen] = useState(false);
  const isInviteDialogOpen = controlledIsOpen ?? internalIsInviteDialogOpen;
  const setIsInviteDialogOpen = onInviteDialogOpenChange ?? setInternalIsInviteDialogOpen;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const updateUser = useUpdateUser();
  const updateInvite = useUpdateInvite();
  const createInvite = useCreateInvite();
  const deleteInvite = useDeleteInvite();

  const handleEditUser = (user: User | (Invite & { isInvite: true })) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedUser(undefined);

    setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 200);
  };

  const handleSubmit = async (data: UserFormValues) => {
    if (!allowedGroupIds.includes(data.userGroup)) {
      toast.error(t('errors.invalidGroup'));
      return;
    }

    if (selectedUser) {
      if ('isInvite' in selectedUser && selectedUser.isInvite) {
        await updateInvite.mutateAsync({
          id: selectedUser.id,
          data: {
            ...data,
            email: undefined,
            customer: resolvedCustomerId,
          },
        });
        toast.success(t('users.userUpdatedSuccessfully'));
      } else {
        const updatedUser = await updateUser.mutateAsync({
          id: selectedUser.id,
          data: {
            ...data,
            language: undefined,
            email: undefined,
            customer: resolvedCustomerId,
          },
        });

        // If the updated user is the current user, update the auth context
        if (selectedUser.id === currentUser?.id) {
          updateAuthUser(updatedUser);
        }

        toast.success(t('users.userUpdatedSuccessfully'));
      }

      handleCloseEditDialog();
    } else {
      await createInvite.mutateAsync({
        ...data,
        customer: resolvedCustomerId,
      });
      setIsInviteDialogOpen(false);
      toast.success(t('users.invitationSentSuccessfully'));
    }
  };

  const handleDelete = async (user: User | (Invite & { isInvite: true })) => {
    if ('isInvite' in user && user.isInvite) {
      await deleteInvite.mutateAsync(user.id);
      toast.success(t('users.invitationDeletedSuccessfully'));
    }
  };

  const handleArchive = async (user: User | (Invite & { isInvite: true })) => {
    if ('isInvite' in user && user.isInvite) {
      return;
    }

    try {
      await updateUser.mutateAsync({
        id: user.id,
        data: {
          archived: true,
        },
      });
      toast.success(t('users.userArchivedSuccessfully'));
    } catch (error) {
      const payloadError = error as { response?: { errors?: Array<{ message?: string }> } };
      const message = payloadError.response?.errors?.[0]?.message;
      if (message === 'LastGmnAdminRoleChange') {
        toast.error(t('users.lastGmnAdminArchive'));
        return;
      }
      if (message === 'LastCustomerAdminRoleChange') {
        toast.error(t('users.lastCustomerAdminArchive'));
        return;
      }
      toast.error(t('errors.generalError'));
    }
  };

  const handleUnarchive = async (user: User | (Invite & { isInvite: true })) => {
    if ('isInvite' in user && user.isInvite) {
      return;
    }

    try {
      await updateUser.mutateAsync({
        id: user.id,
        data: {
          archived: false,
        },
      });
      toast.success(t('users.userUnarchivedSuccessfully'));
    } catch (_error) {
      toast.error(t('errors.generalError'));
    }
  };

  const handleResend = async (invite: User | (Invite & { isInvite: true })) => {
    const toastId = toast.loading(t('users.resendingInvitation'));
    try {
      await payloadClient.customRequest({
        subpath: '/invites/resend-invite',
        method: 'POST',
        data: { inviteId: invite.id },
      });
      toast.success(t('users.invitationResentSuccessfully'), { id: toastId });
    } catch (_error) {
      toast.error(t('errors.generalError'), { id: toastId });
    }
  };

  const isLoading = isLoadingUsers || isLoadingInvites || isLoadingGroups;

  const combinedData = [
    ...(usersData?.docs ?? []),
    ...(statusFilter === 'active'
      ? (invitesData?.docs?.map((invite: Invite) => ({
          ...invite,
          isInvite: true,
        })) ?? [])
      : []),
  ];

  // Filter data based on search term
  const filteredData = debouncedSearchTerm
    ? combinedData.filter((user) => {
        const searchLower = debouncedSearchTerm.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email.toLowerCase();
        return fullName.includes(searchLower) || email.includes(searchLower);
      })
    : combinedData;

  const inviteDialog = (
    <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.inviteUser')}</DialogTitle>
          <DialogDescription>{t('users.inviteUserDescription')}</DialogDescription>
        </DialogHeader>
        <UserForm
          onSubmit={handleSubmit}
          allowedGroups={allowedGroups}
          groupsLoading={isLoadingGroups}
        />
      </DialogContent>
    </Dialog>
  );

  const editDialog = (
    <Dialog open={isEditDialogOpen} onOpenChange={handleCloseEditDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.editUser')}</DialogTitle>
          <DialogDescription>{t('users.editUserDescription')}</DialogDescription>
        </DialogHeader>
        <UserForm
          user={selectedUser}
          onSubmit={handleSubmit}
          allowedGroups={allowedGroups}
          groupsLoading={isLoadingGroups}
        />
      </DialogContent>
    </Dialog>
  );

  const statusTabs = (
    <Tabs
      value={statusFilter}
      onValueChange={(value) => setStatusFilter(value as 'active' | 'archived')}
      className="w-full sm:w-auto"
    >
      <TabsList className="grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
        <TabsTrigger value="active">{t('users.activeUsersTab')}</TabsTrigger>
        <TabsTrigger value="archived">{t('users.archivedUsersTab')}</TabsTrigger>
      </TabsList>
    </Tabs>
  );

  const content = (
    <>
      <TableCard className="mb-4">
        <TableCardHeader>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <TableSearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t('users.searchPlaceholder')}
            />
            {statusTabs}
          </div>
        </TableCardHeader>
      </TableCard>

      <TableCard>
        <TableCardContent>
          <UserTable
            users={filteredData}
            onEdit={handleEditUser}
            onDelete={handleDelete}
            onResend={handleResend}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
            isLoading={isLoading}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </TableCardContent>
      </TableCard>

      {inviteDialog}
      {editDialog}
    </>
  );

  // If embedded mode, return only table and dialogs (no wrapper, no search)
  if (embedded) {
    return (
      <>
        <div className="border-b border-border bg-muted/30 px-6 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm font-medium text-muted-foreground">{t('common.status')}</div>
            {statusTabs}
          </div>
        </div>
        <UserTable
          users={filteredData}
          onEdit={handleEditUser}
          onDelete={handleDelete}
          onResend={handleResend}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          isLoading={isLoading}
          sorting={sorting}
          onSortingChange={setSorting}
        />
        {inviteDialog}
        {editDialog}
      </>
    );
  }

  // Full page mode
  return (
    <PageContainer>
      <PageTitle
        icon={Users}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('navigation.items.gmnSettings')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>{t('navigation.items.gmnUsers')}</SimpleBreadcrumbItem>
          </>
        }
        title={title || t('users.users')}
        primaryAction={
          !hideInviteButton
            ? {
                label: t('users.inviteUser'),
                onClick: () => setIsInviteDialogOpen(true),
                icon: <Plus size={16} />,
              }
            : undefined
        }
      />

      {content}
    </PageContainer>
  );
}
